import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/authStore';
import { getBackendOrigin } from '@/utils/apiOrigin';

const SOCKET_URL = getBackendOrigin();

let socket: Socket | null = null;
let intentionalDisconnect = false;

function hasSocketSession(): boolean {
  const { authReady, token, isAuthenticated, sessionUsesCookie } = useAuthStore.getState();
  if (!authReady) return false;
  if (token) return true;
  return Boolean(isAuthenticated && sessionUsesCookie);
}

export function connect() {
  if (!hasSocketSession()) return;

  const token = useAuthStore.getState().token;
  if (!token) return;

  if (socket?.connected) return;

  intentionalDisconnect = false;

  socket = io(SOCKET_URL, {
    path: '/api/socket.io',
    auth: { token },
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    timeout: 10000,
  });

  socket.on('connect', () => {
    console.log('[Socket] Connected:', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    if (!intentionalDisconnect) {
      console.log('[Socket] Disconnected:', reason);
    }
  });

  socket.on('connect_error', (err) => {
    if (intentionalDisconnect) return;

    const isAuthError =
      err.message === 'Invalid token' || err.message === 'Authentication required';

    if (isAuthError) {
      console.warn('[Socket] Auth failed — reconexão suspensa até novo token.');
      socket?.disconnect();
      socket = null;
    } else {
      console.warn('[Socket] Connection error:', err.message);
    }
  });
}

export function reconnect() {
  disconnect();
  connect();
}

export function disconnect() {
  intentionalDisconnect = true;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function on(event: string, callback: (...args: any[]) => void) {
  socket?.on(event, callback);
}

export function off(event: string, callback?: (...args: any[]) => void) {
  socket?.off(event, callback);
}

export function emit(event: string, data?: any) {
  socket?.emit(event, data);
}

export function getSocket(): Socket | null {
  return socket;
}
